import { differenceInSeconds, parseISO } from "date-fns";
import _isBoolean from "lodash/isBoolean";
import { dateFromDatetime, timeFromDatetime } from "misc/formatHelpers";

export function findInvoiceByIds(invoices, ids) {
  let foundInvoice = null;

  const scanForInvoice = (invoice) => {
    if (foundInvoice) return;

    if (ids.includes(invoice.id)) {
      foundInvoice = invoice;
    } else {
      invoice.children.forEach(scanForInvoice);
    }
  };

  invoices.forEach(scanForInvoice);

  return foundInvoice;
}

export function findInvoiceIdForBooking(booking, invoiceIds = []) {
  const invoices = booking.invoices || [];
  const invoice = findInvoiceByIds(invoices, invoiceIds);

  return invoice && invoice.id;
}

export function adaptFirstHistoryEntry(entries, record, resource) {
  const isCreateEntry = (entry) => entry.action === "+" && entry.relation_name === null;
  const createEntryIndex = entries.findIndex(isCreateEntry);

  if (createEntryIndex === -1) {
    const superficialCreateEntry = {
      id: 0,
      action: "+",
      relation_name: null,
      occured_at: record.created_at,
      changed_by: undefined,
      resource,
    };

    return entries.concat(superficialCreateEntry);
  } else {
    return entries;
  }
}

export function shouldHistoryExpand(resource, fieldChanges = []) {
  return (
    resource === "notes" ||
    resource === "descriptions" ||
    (resource === "event_bases" && fieldChanges.find((f) => f.name === "summary"))
  );
}

function dateTimeChangeToDayChange(change) {
  const { old_value, new_value } = change;
  const oldDay = dateFromDatetime(old_value);
  const newDay = dateFromDatetime(new_value);

  if (oldDay !== newDay) {
    return { name: "day", old_value, new_value };
  }
}

function transformedEventDateChanges(oldChanges) {
  const isDatetimeChange = (c) => c.name === "start_datetime" || c.name === "end_datetime";
  const oldDatetimeChanges = oldChanges.filter(isDatetimeChange);
  const otherChanges = oldChanges.filter((c) => !isDatetimeChange(c));

  const newDatetimeChanges = [];

  oldDatetimeChanges.forEach((change) => {
    if (change.name === "start_datetime") {
      const dayChange = dateTimeChangeToDayChange(change);

      if (dayChange) {
        newDatetimeChanges.push(dayChange);
      }
    }

    const oldTime = timeFromDatetime(change.old_value);
    const newTime = timeFromDatetime(change.new_value);

    if (oldTime !== newTime) {
      newDatetimeChanges.push(change);
    }
  });

  return newDatetimeChanges.concat(otherChanges);
}

export function preprocessFieldChanges(relationName, fieldChanges) {
  if (relationName === "dates") {
    return transformedEventDateChanges(fieldChanges);
  } else if (relationName === "prices") {
    return fieldChanges.filter((fc) => fc.name !== "hashed_positions");
  } else if (relationName === "bookings" || relationName === "invoices") {
    return [];
  } else {
    return fieldChanges;
  }
}

export function getResourceFromRelationName(relationName, originResource) {
  if (relationName === "dates") {
    return "event_dates";
  } else if (
    originResource === "event_bases" &&
    (relationName === "group_children" || relationName === "group_parent")
  ) {
    return "event_bases";
  } else if (originResource === "events" && (relationName === "group_children" || relationName === "group_parents")) {
    return "events";
  } else if (relationName === "event_lecturers") {
    return "events";
  } else if (relationName === "rooms") {
    return "event_rooms";
  } else if (relationName === "price__positions") {
    return "price_positions";
  } else if (relationName === "lecturer__qualifications") {
    return "qualifications";
  } else if (relationName === "attendee__custom_fields_data") {
    return "custom_fields_data";
  } else if (
    ["attendee", "auth_user", "price", "moodle_user", "event_feedback_form", "lecturer"].includes(relationName)
  ) {
    return `${relationName}s`;
  } else {
    return relationName || originResource;
  }
}

function shouldBatchTimelineData(entry, nextEntry, type) {
  const occuredAroundTheSameTime = (entry, nextEntry) =>
    differenceInSeconds(parseISO(entry.occured_at), parseISO(nextEntry.occured_at)) < 5 * 60;

  const additionalChecks = (entry, nextEntry) => {
    if (type === "emails") {
      return (
        (entry.record.external_template && entry.record.external_template === nextEntry.record.external_template) ||
        (entry.record.subject && entry.record.subject === nextEntry.record.subject)
      );
    } else if (
      type === "event_attendances" &&
      (entry.action !== "+" || entry.record.event_date_day !== nextEntry.record.event_date_day)
    ) {
      return false;
    } else {
      return true;
    }
  };

  return (
    nextEntry &&
    nextEntry.relation_name === type &&
    entry.action === nextEntry.action &&
    occuredAroundTheSameTime(entry, nextEntry) &&
    additionalChecks(entry, nextEntry)
  );
}

function maybeBatchTimelinedata(timelineData, index, entry, type) {
  const newIndex = index + 1;
  const nextEntry = timelineData[newIndex];

  if (shouldBatchTimelineData(entry, nextEntry, type)) {
    let newEntry = entry;

    if (newEntry.batch_data) {
      newEntry.batch_data.push(nextEntry);
    } else {
      newEntry = { ...entry, batch_data: [entry, nextEntry] };
    }

    return maybeBatchTimelinedata(timelineData, newIndex, newEntry);
  } else {
    return [[entry], newIndex];
  }
}

function shouldSkipTimelineEntry(entry) {
  const notRecordCreateEntry = !(entry.relation_name === null && entry.action === "+");
  // const entryWithoutUser = entry.changed_by === null && entry.relation_name !== "emails";
  const attendeeCreateEntry = entry.relation_name === "attendee" && entry.action === "+";
  const priceCreateEntry = entry.relation_name === "price" && entry.action === "+";

  if (entry.relation_name === "emails" && entry.record && entry.record.is_test) {
    return true;
  }

  if (entry.changed_by === null && entry.relation_name != "emails") {
    return true;
  }

  return notRecordCreateEntry && (attendeeCreateEntry || priceCreateEntry);
}

function prependNewEntries(originalEntry, changesToSplit, otherChanges) {
  const newEntries = changesToSplit.map((fc) => ({ ...originalEntry, field_changes: [fc] }));

  if (otherChanges.length > 0) {
    const updatedEntry = { ...originalEntry, field_changes: otherChanges };
    return [newEntries, updatedEntry];
  } else {
    return [newEntries, null];
  }
}

function splitChanges(entry, splitFunction) {
  const changesToSplit = entry.field_changes.filter(splitFunction);
  const otherChanges = entry.field_changes.filter((fc) => !splitFunction(fc));

  return [changesToSplit, otherChanges];
}

function createEntriesForBooleanFieldChanges(entry) {
  const [booleanChanges, otherChanges] = splitChanges(entry, (fc) => _isBoolean(fc.new_value));
  return prependNewEntries(entry, booleanChanges, otherChanges);
}

function createEntriesForFieldChangesToSplit(entry, resource) {
  if (entry === null) return [[], null];
  let changesToSplit = [];
  let otherChanges = null;

  if (resource === "events" && entry.relation_name === null) {
    [changesToSplit, otherChanges] = splitChanges(entry, (fc) => fc.name === "status");
  } else if (resource === "event_bases") {
    [changesToSplit, otherChanges] = splitChanges(entry, (fc) => fc.name === "event_type");
  } else {
    otherChanges = entry.field_changes;
  }

  return prependNewEntries(entry, changesToSplit, otherChanges);
}

export function preprocessTimelineData(timelineData, resource) {
  const newTimelineData = [];

  for (let i = 0; i < timelineData.length; ) {
    const entry = { ...timelineData[i] };
    let entriesToAppend = [entry];
    let newIndex = i + 1;

    if (shouldSkipTimelineEntry(entry)) {
      i = newIndex;
      continue;
    } else if (entry.relation_name === "emails" && resource === "events") {
      [entriesToAppend, newIndex] = maybeBatchTimelinedata(timelineData, i, entry, "emails");
    } else if (entry.relation_name === "event_attendances") {
      [entriesToAppend, newIndex] = maybeBatchTimelinedata(timelineData, i, entry, "event_attendances");
    } else if (entry.action === "~") {
      const [booleanEntries, entryWithoutBooleanChanges] = createEntriesForBooleanFieldChanges(entry);
      const [splitEntries, entryWithoutSplitChanges] = createEntriesForFieldChangesToSplit(
        entryWithoutBooleanChanges,
        resource,
      );

      entriesToAppend = [...booleanEntries, ...splitEntries];

      if (entryWithoutSplitChanges) {
        entriesToAppend = entriesToAppend.concat(entryWithoutSplitChanges);
      }
    }

    entriesToAppend.forEach((entry) => {
      entry.resource = getResourceFromRelationName(entry.relation_name, resource);
      newTimelineData.push(entry);
    });

    i = newIndex;
  }

  return newTimelineData;
}

export function filterHistoryData(filterValues, data) {
  const { changedBy, from, to, resource } = filterValues;
  const changedById = parseInt(changedBy);

  if (!changedById && !from && !to && !resource) {
    return null;
  }

  const filterByResource = (resource, entry) => {
    if (!resource) return true;
    return entry.resource && entry.resource == resource;
  };

  const filterByUser = (changedById, entry) => {
    if (!changedById) return true;
    return entry.changed_by && entry.changed_by.id == changedById;
  };

  const filterByFrom = (from, entry) => {
    if (!from) return true;
    return parseISO(entry.occured_at) > parseISO(from);
  };

  const filterByTo = (to, entry) => {
    if (!to) return true;
    return parseISO(entry.occured_at) < parseISO(to);
  };

  const filterChain = (entry) =>
    filterByResource(resource, entry) &&
    filterByUser(changedById, entry) &&
    filterByFrom(from, entry) &&
    filterByTo(to, entry);

  return data.filter(filterChain);
}

export function hasTextHeavyChanges(resource, fieldChanges = []) {
  return (
    resource === "descriptions" ||
    resource === "custom_fields_data" ||
    (resource === "notes" && fieldChanges.find((f) => f.name === "text")) ||
    (resource === "event_bases" && fieldChanges.find((f) => f.name === "summary"))
  );
}

export function recordIsGroupChild(record) {
  return !!record && !!record.group_parents && record.group_parents.length > 0;
}

export function getRelatedEntitiesForEmail(bookings, interests, attendees, lecturers) {
  let relatedEntities, relatedEntityType;
  if (bookings.length > 0) {
    relatedEntities = bookings;
    relatedEntityType = "booking";
  } else if (interests.length > 0) {
    relatedEntities = interests;
    relatedEntityType = "interest";
  } else if (lecturers.length > 0) {
    relatedEntities = lecturers;
    relatedEntityType = "lecturer";
  } else {
    relatedEntities = attendees;
    relatedEntityType = "attendee";
  }

  return { relatedEntities, relatedEntityType };
}
